/** `rule.fallDamage` — hard landings. The drop is measured from the last
 * standing height (take-off, not the jump apex) to the landing, either in
 * world units (`height`) or in surface LEVELS passed (`levels`: distinct
 * surface tops crossed, the landing one included — "fell three floors").
 * A drop ≥ `killAt` sends a `damage` signal (core.vitals decides); a drop ≥
 * `dazeAt` dazes: the module HOLDS the body (the `heldBy` protocol) in the
 * hit pose for `dazeTicks`. Held, hidden or frozen bodies (ladders, rides,
 * respawns) restart the measurement where they are. */
import { int, num, obj, oneOf } from '../schemaBuilders.js';
import { defineModule, num as read, type ModuleCtx } from '../module.js';
import { heldBy, surfaces } from '../physics.js';
import type { EntityV1 } from '../world.js';

export type FallDamageConfig = {
  measure: 'height' | 'levels';
  dazeAt: number;
  killAt: number;
  /** 0 disables the daze. */
  dazeTicks: number;
};

const LEVEL_EPS = 0.05;

function dropOf(ctx: ModuleCtx<FallDamageConfig>, from: number, to: number): number {
  if (ctx.config.measure === 'height') return to - from;
  const tops: number[] = [];
  for (const s of surfaces(ctx)) {
    if (s.top > from + LEVEL_EPS && s.top <= to + LEVEL_EPS && !tops.some(t => Math.abs(t - s.top) < LEVEL_EPS)) tops.push(s.top);
  }
  return tops.length;
}

export const fallDamage = defineModule<FallDamageConfig>({
  id: 'rule.fallDamage', version: 1, attach: 'entity', role: 'rule',
  summary: 'Hard landings: a drop (height or surface levels, from take-off) dazes the faller in place or, past the kill threshold, costs a life.',
  config: obj({ measure: oneOf('height', 'levels'), dazeAt: num(0, 64), killAt: num(0.5, 64), dazeTicks: int(0, 300) }),
  requires: ['body.platformer', 'core.vitals'], events: ['daze', 'fatal-fall'],
  presentation: { daze: { cue: 'impact', vfx: { effect: 'burst', palette: 'slot', count: 6 } }, 'fatal-fall': { cue: 'none', vfx: { effect: 'shake' } } },
  proofs: ['short-drop-harmless', 'daze-holds-in-place', 'kill-sends-damage', 'measured-from-takeoff-not-apex', 'levels-count-distinct-tops',
    'held-or-respawned-restarts-measure'],
  includeHidden: true,
  animStates: c => (c.dazeTicks > 0 ? ['hit'] : []),
  check: c => (c.dazeTicks > 0 && c.dazeAt >= c.killAt ? ['dazeAt must be below killAt'] : []),
  init(ctx, e) { Object.assign(ctx.data(e), { ref: e.y, airborne: false, active: e.status === 'active', daze: 0, hold: false }); },
  systems: {
    // Collide: after every movement module landed the body, before vitals reads damage.
    collide(ctx, e) {
      if (!e) return;
      const c = ctx.config, d = ctx.data(e);
      const restart = (target: EntityV1) => { d.ref = target.y; d.airborne = false; };
      if (e.status !== 'active') { restart(e); Object.assign(d, { active: false, daze: 0, hold: false }); return; }
      if (heldBy(e, 'rule.fallDamage')) { restart(e); return; }
      if (d.active !== true) { restart(e); d.active = true; }
      if (read(d, 'daze') > 0) {
        d.daze = read(d, 'daze') - 1; d.hold = read(d, 'daze') > 0; restart(e);
        return;
      }
      if (!e.grounded) { d.airborne = true; return; }
      if (d.airborne === true) {
        const drop = dropOf(ctx, read(d, 'ref'), e.y);
        if (drop >= c.killAt) {
          ctx.signal('damage', e, null, { amount: 1, x: e.x, y: e.y - e.h / 2, tags: [...e.tags] });
          ctx.emit('fatal-fall', { x: e.x, y: e.y, slot: e.slot ?? 0, drop });
        } else if (c.dazeTicks > 0 && drop >= c.dazeAt) {
          d.daze = c.dazeTicks; d.hold = true; e.vx = 0;
          ctx.emit('daze', { x: e.x, y: e.y - e.h / 2, slot: e.slot ?? 0, drop });
        }
      }
      restart(e);
    },
    present(ctx, e) {
      if (!e || e.status !== 'active' || read(ctx.data(e), 'daze') <= 0) return;
      ctx.setAnim(e, 'hit');
      ctx.react(e, 'flicker');
    },
  },
});
